import type { CommerceDataSource } from "@teqtank/commerce-contracts";
import type { CommerceTransport } from "@teqtank/commerce-transport";

const STORAGE_PREFIX = "teqtank-commerce-session:v1";

export interface PersistedCommerceSessionTarget {
    readonly commerceKey: string;
    readonly dataSource: CommerceDataSource;
    readonly transport: CommerceTransport;
}

export function readPersistedCommerceSession(
    target: PersistedCommerceSessionTarget,
): string | undefined {
    const storage = getSessionStorage();
    const key = createStorageKey(target);
    if (storage === undefined || key === undefined) return undefined;

    try {
        const token = storage.getItem(key)?.trim();
        return token === undefined || token.length === 0 ? undefined : token;
    } catch {
        return undefined;
    }
}

export function writePersistedCommerceSession(
    target: PersistedCommerceSessionTarget,
    token: string,
): void {
    const storage = getSessionStorage();
    const key = createStorageKey(target);
    if (storage === undefined || key === undefined) return;

    try {
        storage.setItem(key, token);
    } catch {
        // Storage can be unavailable through browser policy or quota. The
        // runtime remains usable for the current document in that case.
    }
}

export async function clearPersistedCommerceSession(
    target: PersistedCommerceSessionTarget,
): Promise<void> {
    const storage = getSessionStorage();
    const key = createStorageKey(target);
    if (storage !== undefined && key !== undefined) {
        try {
            storage.removeItem(key);
        } catch {
            // Clearing an unavailable browser store is intentionally idempotent.
        }
    }

    await target.transport.clearSessionPersistence?.({
        commerceKey: target.commerceKey,
        dataSource: target.dataSource,
    });
}

function createStorageKey(target: PersistedCommerceSessionTarget): string | undefined {
    const scope = target.transport.sessionPersistenceScope?.trim();
    if (scope === undefined || scope.length === 0) return undefined;

    return [
        STORAGE_PREFIX,
        encodeURIComponent(scope),
        encodeURIComponent(target.commerceKey.trim()),
        target.dataSource,
    ].join(":");
}

function getSessionStorage(): Storage | undefined {
    try {
        return typeof globalThis.sessionStorage === "object"
            ? globalThis.sessionStorage
            : undefined;
    } catch {
        return undefined;
    }
}
